import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OrganisationUser, RequiredOrganisationUser } from '../models/auth-user';
import { ISignUpForm } from './authentication.service';
import { IAPIResponse } from './dashboard.service';
import { TimeZoneService } from './time-zone.service';
import { LoggedInUser, LoginResponseForAdmin, ManagerialOperationsService } from './managerial-operations.service';
import { IManagerAccess } from '../models/manager-access';

export interface ILoginCredentials {
  email: string;
  password: string;
}
 
export interface IAuthResponse {
  statusCode:number;
  message: string;
  data?: any;
}
 
 
export class LoginResponseData {
  accessToken: string;
  refreshToken: string;
  userDetails: OrganisationUser | LoggedInUser | RequiredOrganisationUser;
  loggedInUser: any;

}
 
export class LoginResponse {
  statusCode: number;
  message:string;
  data: LoginResponseData;
}
 
@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
 
  private url = `${environment.apiEndpoint}/api/v1/organisation-management/organisations`;
  
    private isTokenRefreshing = false;
    /**
     * A dying session produces MANY 401s at once — one per request the page had
     * in flight — and several paths react by calling logOut(). Without this the
     * tear-down runs once per 401: a burst of /logout posts, each 401ing in turn.
     */
    private isLoggingOut = false;
    private newTokenSubject = new BehaviorSubject<string|null>(null);
  
  
  public get IsTokenRefreshing(){
    return this.isTokenRefreshing;
  }
  public get NewTokenSubject(){
    return this.newTokenSubject;
  }
  
  constructor(
    private httpService: HttpClient,
    private router: Router,
    private timeZoneService: TimeZoneService,
    private managerService: ManagerialOperationsService
  ) { }

    public getCurrenUser(): OrganisationUser | LoggedInUser | null {
      const userString = this.getLoggedInUser();
      if (userString == null) {
        return null;
      }
      try {
          const temp = JSON.parse(userString);

          // fullName and designationId are present if
          // user is manager or employee.
          // We handle this here after unifying login.
          if (temp.fullName && temp.designationId) {
              const loggedInUser = new LoggedInUser();
              loggedInUser.email = temp.email;
              loggedInUser.designationId = temp.designationId;
              loggedInUser.designationName = temp.designationName;
              loggedInUser.fullName = temp.fullName;
              loggedInUser.id = temp.id;
              loggedInUser.roleId = temp.roleId;
              loggedInUser.organisationCode = temp.organisationCode;
              loggedInUser.customTaskName = temp.customTaskName;
              loggedInUser.mobileOrg = temp.mobileOrg;
              loggedInUser.isRestricted = temp.isRestricted;
              return loggedInUser;
          }
          const loggedInUser = new OrganisationUser();
          loggedInUser.name = temp.name;
          loggedInUser.setupCompleted = temp.setupCompleted;
          loggedInUser.email = temp.email;
          loggedInUser.id = temp.id;
          loggedInUser.roleId = temp.roleId;
          loggedInUser.organisationCode = temp.organisationCode;
          loggedInUser.customTaskName = temp.customTaskName;
          loggedInUser.subscriptionPlanId = temp.subscriptionPlanId;
          loggedInUser.upgradePlanWarning = temp.upgradePlanWarning;
          loggedInUser.trialEndDate = temp.trialEndDate;
          loggedInUser.isInvoicePending = temp.isInvoicePending;
          loggedInUser.employeeId = temp.employeeId;
          loggedInUser.mobileOrg = temp.mobileOrg;
          loggedInUser.isRestricted = temp.isRestricted;
          return loggedInUser;
      } catch (e) {
          console.error(e);
          throw e;
      }
    }
    public getLoggedInUser(): string {
      const userDetails = sessionStorage.getItem('FocusRoAuthUserDetailsProvider');
      if (userDetails) {
        return userDetails;
      }
      return localStorage.getItem('FocusRoAuthUserDetailsProvider');
    }
    
    public storeLoggedInUser(data: LoginResponseData, rememberSession: boolean) {
      if (rememberSession) {
        localStorage.setItem('FocusRoAuthUserDetailsProvider', JSON.stringify(data.userDetails));
        localStorage.setItem('FocusRoAuthUserToken', data.accessToken);
      } else {
        sessionStorage.setItem('FocusRoAuthUserDetailsProvider', JSON.stringify(data.userDetails));
        sessionStorage.setItem('FocusRoAuthUserToken', data.accessToken);
      }
    }
    private authenticate(credentials: ILoginCredentials) {
      const reqUrl = `${this.url}/login`;
      return this.httpService.post<LoginResponse>(reqUrl, credentials);
    }
    private selectOrgToLogin(organisationCode, idToken: string) {
      const reqUrl = `${this.url}/select-org`;
      return this.httpService.post<LoginResponse>(reqUrl, organisationCode, { headers: new HttpHeaders({
        Authorization: `Bearer ${idToken}`,
    })});
    }
  /**
   * Signs in an user in to the app using the aws cognito service.
   * @param userEmail Email address the user uses to sign in
   * @param password Password the user uses to sign in
   */
  public login(credentials: ILoginCredentials, rememberSession: boolean) {
    return this.authenticate(credentials).pipe(
      map((response) => {
        if('requiresOrganisationSelection' in response.data.userDetails){
          return response;
        }
        else if((response.data.userDetails as LoggedInUser).designationId){
          const userDetails = response.data.userDetails as LoggedInUser;
          this.managerService.storeLoggedInUser(
              {
                  loggedInUser: {
                      customClientName: userDetails.customClientName,
                      customProjectName: userDetails.customProjectName,
                      customTaskName: userDetails.customTaskName,
                      designationId: userDetails.designationId,
                      designationName: userDetails.designationName,
                      email: userDetails.email,
                      fullName: userDetails.fullName,
                      id: userDetails.id,
                      mobileOrg: userDetails.mobileOrg,
                      isRestricted: userDetails.isRestricted,
                      organisationCode: userDetails.organisationCode,
                      orgTimezone: userDetails.orgTimezone,
                      roleId: userDetails.roleId,
                      setupCompleted: userDetails.setupCompleted,
                      employeeTimezone: userDetails.employeeTimezone,
                  },
                  refreshToken: response.data.refreshToken,
                  token: response.data.accessToken,
              },
              rememberSession
          );
          const intervel=this.managerService.getRefreshTokenIntervelForManager();
          // console.log("interval 5",intervel);
          setTimeout(()=>{
            this.managerService
                .refreshTokenForManager(userDetails.organisationCode)
                .subscribe();
          },intervel);
        }else{
          this.storeLoggedInUser(response.data, rememberSession);
          this.timeZoneService.setTimeZone(response.data.userDetails.orgTimezone);
          const intervel=this.getRefreshTokenIntervel();
          setTimeout(()=>{
            this.refreshToken().subscribe();
          },intervel);
        }
      })
    );
  }
  public getAuthToken(): string | null {
    const token = sessionStorage.getItem('FocusRoAuthUserToken');
    if (token) {
      return token;
    }
    return localStorage.getItem('FocusRoAuthUserToken');
  }
  public isLoggedIn() {
    if (this.getLoggedInUser()) {
      return true;
    }
    return false;
  }
  public logOut(userId: number,noRedirect?: boolean): void;
  public logOut(userId,noRedirect = false): void {
    // Tear down once, however many callers ask.
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    const reqUrl = `${this.url}/logout`;
    const reqBody = {
                      userId : userId                     
                    };
    this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        this.completeLogOut(noRedirect);
      },(error) => {
        // The server call is best-effort: signing out locally must not depend on
        // it succeeding. It cannot succeed when the session is already dead —
        // /logout is itself authenticated, so an expired session answers 401 —
        // and leaving the tokens in storage on that path kept isLoggedIn() true,
        // which sent the error interceptor round again: 401 -> refresh -> 401 ->
        // logOut -> 401 ... forever. Only reachable once the REFRESH token has
        // expired too, which an ordinary ten-day session never does, so it stayed
        // hidden until support sessions started expiring both at once.
        console.log(error.error);
        this.completeLogOut(noRedirect);
      });
  }

  /** Drop every trace of the session locally. Safe to run more than once. */
  private completeLogOut(noRedirect: boolean): void {
    localStorage.removeItem('FocusRoAuthUserDetailsProvider');
    localStorage.removeItem('FocusRoAuthUserToken');
    sessionStorage.removeItem('FocusRoAuthUserDetailsProvider');
    sessionStorage.removeItem('FocusRoAuthUserToken');
    if (!noRedirect) {
      this.router.navigateByUrl('/auth/login');
    }
    this.clearCacheAndRedirect();
  }
  private clearCacheAndRedirect(): void {
    window.location.reload();
  }
  public initPasswordReset(userEmail: string, organisationCode: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/forget_password`;
      this.httpService.post<IAuthResponse>(reqUrl, {email:userEmail.trim(), organisationCode})
        .subscribe((response) => {
          observer.next(response);
        }, (error) => {
          observer.error({
            message: 'unable to send password reset code',
            error: error.error
          });
        });
    });
  }
 
  public changePassword(resetCode: number, newPassword: string, email: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/reset_password`;
      const reqBody = {
                        password_reset_code : resetCode,
                        new_password : newPassword.trim(),
                        email : email.trim()
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        observer.next(response);
      }, (error) => {
        observer.error({
          message: 'unable to change the password',
          error: error.error
        });
      });
    });
  }
  public signUp(request: ISignUpForm) {
    return new Observable((observer) => {
      const reqUrl = `${this.url}/register`;
      const reqBody = {
                        name : request.name,
                        organisationCode: request.nickName,
                        email : request.email,
                        password : request.password
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        observer.next(response);
        },
          (error) => {
            observer.error({
              message: 'sign up failed',
              error: error.error
            });
          });
    });
  }
  public verifySignUp(userEmail: string, token: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/verify_email`;
      const reqBody = {
                        email : userEmail.trim(),
                        verification_code : token                        
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Verification Failed',
            error: error.error
          });
        });
       
    });
 
  }
 
  public resendVerifcationCode(userEmail: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/resend_code`;
      const reqBody = {
                        email : userEmail.trim()                    
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Unable to Resend Verification Code',
            error: error.error
          });
        })
    });
  }
  public refreshToken()
  {
    if (this.isTokenRefreshing) {
        return this.newTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap(() => {
                return new Observable((observer) => {
                    let token: string = null;
                    if (localStorage.getItem('FocusRoAuthUserToken')) {
                        token = localStorage.getItem(
                            'FocusRoAuthUserToken'
                        );
                    } else if (
                        sessionStorage.getItem('FocusRoAuthUserToken')
                    ) {
                        token = sessionStorage.getItem(
                            'FocusRoAuthUserToken'
                        );
                    }
                    observer.next(token);
                    observer.complete();
                });
            })
        );
    }

    console.log(
        '%c🙇 Requesting new Access token%c',
        'color: white; background:rgb(78, 5, 91); padding: 2px 6px; border-radius: 4px; font-weight: bold;'
    );
    this.isTokenRefreshing = true;
    this.newTokenSubject.next(null);

    return  new Observable((observer) => {
    const reqUrl = `${this.url}/refreshToken`;
    this.httpService.post<IAuthResponse>(reqUrl, {})
        .subscribe((response) => {
            const accessToken= response.data.accessToken;
            if(localStorage.getItem("FocusRoAuthUserToken"))
            {
              localStorage.setItem('FocusRoAuthUserToken', accessToken);
            }
            else if(sessionStorage.getItem("FocusRoAuthUserToken"))
            {
              sessionStorage.setItem('FocusRoAuthUserToken',accessToken);
            }
            const intervel=this.getRefreshTokenIntervel();
            setTimeout(()=>{
              this.refreshToken().subscribe();
            },intervel);

            this.isTokenRefreshing = false;
            this.newTokenSubject.next(accessToken);
            observer.next(accessToken);
        }, (error) => {
            console.log(error.error);

            this.isTokenRefreshing = false;
            // Release everything queued behind this refresh, then REPLACE the
            // subject: an errored RxJS subject is dead for good, so every later
            // request that waits on it would hang instead of failing — which is
            // what left requests pending forever once a session expired.
            this.newTokenSubject.error(error.error);
            this.newTokenSubject = new BehaviorSubject<string | null>(null);

            let message = null;
            if (error && error.error) {
              message = error.error.message;
            }
            observer.error(message);
            // Null once the tokens are already gone — reading .id would throw
            // here, inside an error handler, and lose the logout entirely.
            const user = this.getCurrenUser();
            let userId = null;
            if (user) {
              userId = user.id;
            }
            this.logOut(userId);
        });
      }
    );
  }
  public checkTokenExpired(token: string)
  {
    const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
    return (Math.floor((new Date).getTime() / 1000)) >= expiry;
  }
  // public getTokenExpireTime(token: string)
  // {
  //   console.log('token:', token)
  //   console.log('token first split:', token.split('.'))
  //   console.log('token:', token.split('.')[1])
  //   console.log('token split atob:', (atob(token.split('.')[1])))
  //   console.log('JSON Parsed Token expire Time :', (JSON.parse(atob(token.split('.')[1]))).exp)
  //   const expire_time = (JSON.parse(atob(token.split('.')[1]))).exp;
  //   // const expire_time = (JSON.parse(window.atob(token.split('.')[1]))).exp;
  //   // const expire_time = (JSON.parse(window.atob(token.replace(/-/g, "+").replace(/_/g, "/")))).exp;
  //   console.log('exp', expire_time)
  //   return expire_time;
  // }
  public getTokenExpireTime(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(base64Url.length + (4 - base64Url.length % 4) % 4, '=');
      const expire_time = (JSON.parse(atob(base64))).exp;
      return expire_time;
    } catch (error) {
      console.error("Error decoding token: ", error);
      // Handle the error accordingly
      return null;
    }
  }
  public getRefreshTokenIntervel()
  {
    const currentTime = Date.now();
    const token = this.getAuthToken();  
    const expireTime = this.getTokenExpireTime(token)*1000;
    const time = expireTime - currentTime -(60*1000);//refresh token one minut before expire;
    return time;
  }

  public verifyDemoToken(orgCode: string, token: string) {
    return new Observable<IAuthResponse>((observer) => {
        const reqUrl = `${this.url}/${orgCode}/verify-demo-token`;
        const reqBody = {
            loginToken: token,
        };
        this.httpService.post<IAuthResponse>(reqUrl, reqBody).subscribe(
            (response) => {
                this.timeZoneService.setTimeZone(
                  response.data.userDetails.orgTimezone
                );
                observer.next(response);
            },
            (error) => {
                observer.error({
                    message: 'Verification Failed',
                    error: error.error,
                });
            }
        );
    });
  }

  public verifyLoginToken(orgCode: string, token: string, type: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/${orgCode}/verify-login-token`;
      // 'support' is the super-admin console's staff-access flow: the server
      // answers it with a short-lived access token and NO refresh token, so
      // opening a client does not evict that client's own admin session.
      // Anything else stays 'normal' — signup email verification in particular
      // is a real login and must keep its refresh token.
      const reqBody = {
                        loginToken : token,
                        loginType: type == 'demo' ? 'demo' : (type == 'support' ? 'support' : 'normal')
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          // 'support' returns the same org-shaped payload as 'org' (userDetails),
          // not the employee-shaped one (loggedInUser) — reading the wrong key
          // here throws before the session is ever stored.
          if(type == 'org' || type == 'demo' || type == 'support'){
            this.timeZoneService.setTimeZone(response.data.userDetails.orgTimezone);
          }else{
            this.timeZoneService.setTimeZone(response.data.loggedInUser.orgTimezone);
          }
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Verification Failed',
            error: error.error
          });
        });
       
    });
  }
 
  public getPabblyLoginUrl(orgCode: string) {
    return this.httpService.get<IAPIResponse<IPabblyLoginResp>>(`${this.url}/${orgCode}/pabbly-login`);
  }
 
  public getStripeLoginUrl(orgCode: string) {
    return this.httpService.get<IAPIResponse<IStripeLoginResp>>(`${this.url}/${orgCode}/invoice-payement-link`);
  }
 
  public getStripeCustomerPortal(orgCode: string) {
    return this.httpService.get<IAPIResponse<IStripeLoginResp>>(`${this.url}/${orgCode}/stripe-customer-portal`);
  }
 
  public getStripeDetails(orgCode: string) {
    return this.httpService.get<IAPIResponse<IOrganisationDetailsResp>>(`${this.url}/${orgCode}/get-stripe-details`);
  }
  public getOrgTypeDetails(orgCode: string) {
    return this.httpService.get<IAPIResponse<IOrganisationTypeResp>>(`${this.url}/${orgCode}/get-org-type`);
  }

  // Manager access switchboard. Its own endpoint rather than a field on get-org-type: that one
  // deliberately avoids opening a tenant connection, and these settings live in a tenant table.
  // Readable by managers — it is the list of what THEY may open, so the menu needs it.
  public getManagerAccess(orgCode: string) {
    return this.httpService.get<IAPIResponse<IManagerAccess>>(
      `${this.url}/${orgCode}/manager-access/settings`);
  }

  public updateManagerAccess(orgCode: string, body: Partial<IManagerAccess>) {
    return this.httpService.put<IAPIResponse<{ updated: boolean }>>(
      `${this.url}/${orgCode}/manager-access/settings`, body);
  }
public loginWithSelectedUser(
  organisationCode: any,
  rememberSession: boolean,
  token: string,
) {
  return this.selectOrgToLogin(organisationCode, token).pipe(
    map((response: LoginResponse | LoginResponseForAdmin) => {
      const userDetails = response.data.userDetails as any;

      const isEmployeeLogin = 'designationId' in userDetails;

      if (isEmployeeLogin) {
        // Handle Employee login
        const employeeUser = userDetails as LoggedInUser;

        this.managerService.storeLoggedInUser(
          {
            loggedInUser: {
              id: employeeUser.id,
              email: employeeUser.email,
              fullName: employeeUser.fullName,
              designationId: employeeUser.designationId,
              designationName: employeeUser.designationName,
              organisationCode: employeeUser.organisationCode,
              roleId: employeeUser.roleId,
              customTaskName: employeeUser.customTaskName,
              customClientName: employeeUser.customClientName,
              customProjectName: employeeUser.customProjectName,
              orgTimezone: employeeUser.orgTimezone,
              mobileOrg: employeeUser.mobileOrg,
              isRestricted: employeeUser.isRestricted,
              setupCompleted: employeeUser.setupCompleted,
              employeeTimezone: employeeUser.employeeTimezone,
            },
            refreshToken: response.data.refreshToken,
            token: response.data.accessToken,
          },
          rememberSession
        );

        const interval = this.managerService.getRefreshTokenIntervelForManager();
        setTimeout(() => {
          this.managerService
            .refreshTokenForManager(employeeUser.organisationCode)
            .subscribe();
        }, interval);

      } else {
        // Handle Admin login
        const adminUser = userDetails as OrganisationUser;

        this.storeLoggedInUser(
            { ...response.data, loggedInUser: null },
            rememberSession
        );

        const intervel = this.getRefreshTokenIntervel();
        setTimeout(() => {
            this.refreshToken().subscribe();
        }, intervel);
      }
    })
  );
}


  
}
 
export interface IPabblyLoginResp{
  token: string;
  access_url: string;
  expires_at: Date;
}
 
export interface IStripeLoginResp{  
  access_url: string;  
}
 
export interface IOrganisationDetailsResp{
  stripeDetails: any;  
}
export interface IOrganisationTypeResp{
  /** organisation.name — what a payslip prints when no payroll entity applies. */
  name?: string;
  liveStreamingModule?: number;
  multipleTimezoneModule?: number;
  notificationCenterModule?: number;
  teamDelegateModule?: number;
  isMobileOrg: number;
  planId?:number;
  imageDownloadHost?:string;
  allowPayrollOption?:number;
  isPayroll?:number;
  isBiometricEnabled?: boolean | number;
  // Orgs with no desktop/mobile monitoring-agent activity (e.g. biometric-only
  // attendance) — hides agent-sourced fields (Quick Status icon, idle/offline/
  // meeting time, total active/average hours) that are otherwise permanently
  // blank for them. Capability flag, not client-specific. See D-074/F-207.
  noMonitoringAgentModule?: number;
  // Productive Hours (Finanshel premium-client request): when 1, admins may
  // opt individual employees into showing their own EmployeeWorkRecord.productive
  // figure on their Attendance Report. See shouldShowProductiveHoursModule,
  // employees.component.ts (Employee List toggle), attendance-report.component.ts.
  productiveHoursModule?: number;
  // Menu link this org lands on after login (e.g. '/app/analysis/employee-work-status').
  // null/undefined = fall back to the first visible menu. Honoured in
  // MenuService.getFirstMenuLink(), which validates it against the filtered menu.
  defaultLandingPage?: string | null;
  // One-off per-org override for the sidebar "Overview" shortcut label
  // (F-204) — not an entitlement, a single client's text substitution.
  // null/undefined = unchanged (keeps the translated 'menu.OVERVIEW' key).
  // Set only via super-admin, never self-service. See resolveOverviewLabel.
  customOverviewLabel?: string | null;
  // One-off per-org override for the "Work Not Started" text on the
  // Attendance Report Details page (F-205) — not an entitlement, a single
  // client's text substitution. null/undefined = unchanged (keeps that
  // page's own default text). Set only via super-admin, never self-service.
  // See resolveWorkNotStartedLabel. (The Employee Work Status dashboard
  // gets the same override via its own list/overview API responses instead
  // of this session-level payload — see IWorkStatusOverview.)
  customWorkNotStartedLabel?: string | null;
  // Gates which sidebar shell to mount: 1 = mental-model nav rail
  // (RailComponent), 0/undefined = existing flat accordion sidebar. NOT an
  // org_menu_group key — that system controls section visibility within one
  // fixed shell, not which shell to render. See focusro-api's
  // src/app/sql/master-add-nav-rail-enabled-module.sql.
  navRailEnabled?: number;
  // Gates the fixed-header layout on the attendance detail page (CR-030):
  // 1 = header pinned and the day table the only scroll area, 0/undefined =
  // today's whole-page scrolling. Honoured on desktop only — mobile always
  // scrolls normally. See focusro-api's
  // src/app/sql/master-add-sticky-header-enabled.sql.
  stickyHeaderEnabled?: number;
}